<template>
  <div v-loading="downloading" id="recovery-activity" class="brand-card-lg">
    <div v-if="initialLoading">
      <HeaderLoader />
    </div>
    <div v-else>
      <div class="sm:flex sm:items-center sm:justify-between space-y-3 pb-5 sm:space-y-0">
        <div class="space-x-6 flex items-center justify-between flex-grow">
          <div class="inline">
            <p class="title mb-1">Reactivation Activity</p>
          </div>
          <el-button v-if="false" type="plain" class="border-brand-gray-blue border-2 px-4 rounded-md" @click="downloadCampaigns">
            <span class="text-xs text-brand-blue font-semibold">Export Data</span>
          </el-button>
        </div>
      </div>
      <div class="flex justify-between flex-grow mb-1">
        <div class="flex justify-start">
          <div class="max-w-lg flex items-center space-x-2">
            <el-input
              class="recovery-activity-search"
              placeholder="Search for customer by email"
              prefix-icon="el-icon-search"
              clearable
              @clear="search"
              v-model="searchInput"
              @keyup.enter.native="search"
              id="recovery-activity-search"
            ></el-input>
            <el-button :loading="loading && !!searchInput.length" type="primary" class="border-brand-blue border-2 px-4 rounded-md" @click="search">
              <span class="text-xs text-white font-semibold">Search</span>
            </el-button>
          </div>
        </div>
        <div class="flex-col flex space-y-2">
          <div class="flex items-center space-x-2 brand-input" :class="{ 'opacity-50 cursor-not-allowed': searchInput.length > 0 }">
            <DatePickerCondensed
              @change="handleDateChange"
              :dateRange="[startDate, endDate]"
              :disabled="searchInput.length > 0"
            ></DatePickerCondensed>
            <CampaignFilter @update="onFilterUpdate" :disabled="searchInput.length > 0" />
            <!-- sort options -->
            <el-select v-model="sortOrder" placeholder="Sort by" @change="onSortChange" :disabled="searchInput.length > 0">
              <el-option v-for="option in sortOptions" :key="option.value" :value="option.value" :label="option.label"></el-option>
            </el-select>
          </div>
        </div>
      </div>
    </div>
    <div>
      <div v-if="loading && !campaigns.length" class="-ml-4">
        <SessionListLoader />
      </div>
      <div v-if="campaigns.length" class="mt-6">
        <CampaignTable :campaigns="campaigns" />
        <div class="flex justify-center mt-8" v-if="campaigns.length && this.moreCampaigns">
          <el-button type="primary" class="px-8 rounded-md focus:outline-none" @click="fetchNextPage" v-loading="loading">
            <span class="text-sm text-white font-semibold">Load More ↓</span>
          </el-button>
        </div>
      </div>
      <div v-else class="bg-white h-96 relative">
        <div class="text-center absolute h-52 flex items-center justify-center inset-0">
          <el-tooltip effect="dark" placement="bottom-start" popper-class="brand-tooltip">
            <div slot="content">
              <span v-if="filterApplied"> Try changing the filters above. </span>
              <span v-else> We'll have something to show you once more data comes in. </span>
            </div>
            <p class="px-4 py-2 text-brand-blue bg-brand-medium-blue bg-opacity-30 text-sm inline-block rounded-lg font-bold mb-1">
              <span v-if="filterApplied"> No data matches selected filter </span>
              <span v-else> No cancellation insight data yet </span>
            </p>
          </el-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { OFFER_TYPE, DISCOUNT_TYPE, LOAD_STATE, CAMPAIGN_TYPE } from '@/store/shared';
import StatService from '@/api/stats.service';
import { getDateEndpoints } from '@/helpers/date-utils';
import DatePickerCondensed from '@/components/UI/DatePickerCondensed.vue';
import { timeDay } from 'd3-time';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';
import SessionListLoader from '@/components/UI/Loader/SessionListLoader.vue';
import CampaignTable from '@/components/Reactivation/CampaignTable.vue';
import CampaignFilter from '@/components/Reactivation/Analytics/ReactivationCampaignFilter.vue';
import { DOWNLOAD_REACTIVATION_CAMPAIGNS } from '@/store/action-types';

const sortOptions = [
  {
    label: 'Recently Updated',
    value: '-updatedAt',
  },
  {
    label: 'Recently Canceled',
    value: '-subscription.ended_at',
  },
  {
    label: 'Canceled Longest Ago',
    value: 'subscription.ended_at',
  },
  // {
  //   label: 'Recently Reactivated',
  //   value: '-recoverDate',
  // },
];

export default {
  name: 'ReactivationActivity',
  components: {
    DatePickerCondensed,
    HeaderLoader,
    CampaignTable,
    SessionListLoader,
    CampaignFilter,
  },
  data() {
    const defaultStartDate = timeDay.offset(new Date(), -30);
    const defaultEndDate = timeDay.offset(new Date(), 2);
    const [startDate, endDate] = getDateEndpoints([defaultStartDate, defaultEndDate]);
    return {
      OFFER_TYPE,
      DISCOUNT_TYPE,
      filter: {},
      campaigns: [],
      campaignsLoadState: LOAD_STATE.UNLOADED,
      searchInput: this.$route.query.search || '',
      skip: 0,
      limit: 10,
      moreCampaigns: true,
      startDate,
      endDate,
      initialLoading: true,
      sortOrder: '-updatedAt',
      sortOptions,
      downloading: false,
    };
  },
  async created() {
    await this.fetchData();
    if (this.campaigns.length < 8 && this.daysSinceOrgCreated && this.daysSinceOrgCreated > 90 && !this.searchInput) {
      this.startDate = this.orgCreatedDate;
      await this.fetchData();
    }
    this.initialLoading = false;
  },
  computed: {
    loading() {
      return this.campaignsLoadState !== LOAD_STATE.LOADED;
    },
  },
  methods: {
    async fetchData() {
      let mappedFilter = Object.keys(this.filter).reduce((acc, k) => {
        if (this.filter[k].length === 1) {
          acc[k] = this.filter[k][0];
        }
        return acc;
      }, {});
      mappedFilter.startDate = this.startDate;
      mappedFilter.endDate = this.endDate;
      if (this.searchInput.length) {
        mappedFilter = { email: this.searchInput };
      }
      try {
        this.campaignsLoadState = LOAD_STATE.LOADING;
        const campaigns = await StatService.filteredReactivationCampaigns({
          filter: mappedFilter,
          options: { skip: this.skip, limit: this.limit, sort: this.sortOrder },
        });
        this.campaigns.push(...campaigns);
        this.skip += campaigns.length;
        if (campaigns.length < this.limit) {
          this.moreCampaigns = false;
        }
        this.campaignsLoadState = LOAD_STATE.LOADED;
      } catch (e) {
        console.log(e);
        this.campaignsLoadState = LOAD_STATE.ERROR_LOADING;
      }
    },
    handleDateChange(value) {
      this.startDate = value[0];
      this.endDate = value[1];
      this.resetPagination();
      this.fetchData();
    },
    onFilterUpdate(newFilter) {
      this.filter = newFilter;
      this.resetPagination();
      this.fetchData();
    },
    resetPagination() {
      this.campaigns = [];
      this.skip = 0;
      this.moreCampaigns = true;
    },
    fetchNextPage() {
      this.fetchData();
    },
    onSortChange() {
      this.resetPagination();
      this.fetchData();
    },
    search() {
      this.resetPagination();
      this.fetchData();
    },
    filterApplied() {
      return Object.keys(this.filter).length > 0 || this.searchInput.length > 0;
    },
    async downloadCampaigns() {
      try {
        this.downloading = true;
        let mappedFilter = Object.keys(this.filter).reduce((acc, k) => {
          if (this.filter[k].length === 1) {
            acc[k] = this.filter[k][0];
          }
          return acc;
        }, {});
        mappedFilter.startDate = this.startDate;
        mappedFilter.endDate = this.endDate;
        if (this.searchInput.length) {
          mappedFilter = { email: this.searchInput };
        }
        await this.$store.dispatch(DOWNLOAD_REACTIVATION_CAMPAIGNS, {
          filter: mappedFilter,
        });
      } catch (e) {
        this.$message.error('Something went wrong. Please try again later.');
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.recovery-activity-search {
  @apply shadow-sm;
  @apply rounded-md;
  width: 20rem;
  .el-input__icon {
    @apply text-brand-gray-blue;
  }
  .el-input__inner {
    @apply border-brand-gray-blue;
    @apply rounded-lg;
    border-width: 2px;
    line-height: 44px;
    height: 44px;
    &::placeholder {
      @apply text-brand-gray-blue;
    }
  }
}
</style>
